import { useState, useEffect, useMemo } from "react";
import { ArrowLeft, Save, CreditCard } from "lucide-react";
import { useSuppliers } from "../context/SuppliersContext";
import { usePurchaseVoucher } from "../context/PurchaseVoucherContext";
import { useItem } from "../context/ItemContext";
import { useNavigate, useParams } from "react-router-dom";

export default function EditPurchaseVoucher() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { suppliers } = useSuppliers();
  const {
    purchaseVouchers,
    fetchPurchaseVoucherById,
    updatePurchaseVoucher,
  } = usePurchaseVoucher();
  const { items } = useItem();

  const [voucher, setVoucher] = useState(null);
  const [initLoading, setInitLoading] = useState(true);
  const [initError, setInitError] = useState(null);

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedItem, setSelectedItem] = useState("");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [paid, setPaid] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const amount  = Number(quantity || 0) * Number(price || 0);
  const balance = amount - Number(paid || 0);

  // Load the voucher: try the already-fetched list first, else hit the API.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setInitLoading(true);
      setInitError(null);

      try {
        let found = (purchaseVouchers || []).find(
          (v) => String(v.id) === String(id)
        );

        if (!found && fetchPurchaseVoucherById) {
          found = await fetchPurchaseVoucherById(id);
        }

        if (!found) throw new Error("Voucher not found");
        if (cancelled) return;

        setVoucher(found);
        setSelectedItem(String(found.item_id ?? found.itemId ?? ""));
        setQuantity(String(found.quantity ?? ""));
        setPrice(String(found.price ?? ""));
        setPaid(String(found.paid ?? ""));
        setNote(found.note && found.note !== "-" ? found.note : "");
      } catch (err) {
        if (!cancelled) setInitError(err.message || "Failed to load voucher");
      } finally {
        if (!cancelled) setInitLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [id, purchaseVouchers, fetchPurchaseVoucherById]);

  // Resolve supplier from the voucher's user_id once suppliers are available.
  useEffect(() => {
    if (!voucher || !suppliers?.length) return;
    const supplierId = voucher.user_id ?? voucher.userId;
    const match = suppliers.find((s) => String(s.id) === String(supplierId));
    if (match) setSelectedSupplier(match);
  }, [voucher, suppliers]);

  const handleSubmit = async () => {
    if (!selectedItem)       return alert("Select item");
    if (!quantity || !price) return alert("Enter quantity & price");

    const payload = {
      item_id:          selectedItem,
      quantity:         Number(quantity),
      price:            Number(price),
      amount,
      paid:             Number(paid || 0),
      previous_balance: Number(selectedSupplier?.outstanding || 0),
      balance,
      note:             note || "-",
    };

    if (selectedSupplier) {
      payload.user_id = selectedSupplier.id;
      payload.customer_name = `${selectedSupplier.first_name} ${selectedSupplier.last_name}`;
    }

    try {
      setSaving(true);
      await updatePurchaseVoucher(id, payload);
      alert("Purchase Voucher Updated Successfully");
      navigate("/purchase-voucher");
    } catch (err) {
      alert(err.message || "Error updating voucher");
    } finally {
      setSaving(false);
    }
  };

  if (initLoading) {
    return (
      <main className="min-h-screen bg-gray-50 p-4 md:p-6 font-sans flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading voucher…</p>
      </main>
    );
  }

  if (initError) {
    return (
      <main className="min-h-screen bg-gray-50 p-4 md:p-6 font-sans flex flex-col items-center justify-center gap-4">
        <p className="text-red-500 text-sm">{initError}</p>
        <button
          onClick={() => navigate("/purchase-voucher")}
          className="flex items-center gap-2 border border-gray-200 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={15} /> Back to Vouchers
        </button>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 p-4 md:p-6 font-sans">
      <div className="mx-auto max-w-5xl space-y-5">

        {/* Header */}
        <div className="flex justify-between items-center bg-white p-5 rounded-2xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 flex items-center justify-center rounded-xl bg-teal-600 text-white">
              <CreditCard size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Edit Purchase Voucher</h1>
              <p className="text-sm text-gray-400">Update the purchase details below</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/purchase-voucher")}
            className="flex items-center gap-2 border border-gray-200 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={15} /> Back
          </button>
        </div>

        {/* Purchase Form */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 bg-white">
            <h2 className="font-semibold text-gray-700">Purchase Information</h2>
            <p className="text-sm text-gray-400 mt-0.5">Voucher #{id}</p>
          </div>

          <div className="p-6 space-y-6">

            {/* Supplier Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <InfoCard
                label="Supplier Name"
                value={
                  selectedSupplier
                    ? `${selectedSupplier.first_name} ${selectedSupplier.last_name}`
                    : voucher?.customername || voucher?.customer_name || "—"
                }
              />
              <InfoCard label="Phone" value={selectedSupplier?.phone || "—"} />
              <InfoCard label="Email" value={selectedSupplier?.email || "—"} />
              <InfoCard
                label="Previous Balance (Outstanding)"
                value={`₹ ${selectedSupplier?.outstanding ?? voucher?.previous_balance ?? 0}`}
                highlight
              />
            </div>

            {/* Inputs */}
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Item">
                <select
                  value={selectedItem}
                  onChange={(e) => setSelectedItem(e.target.value)}
                  className="w-full mt-2 h-11 border border-gray-200 rounded-xl px-4 bg-gray-50 text-sm outline-none focus:border-teal-400"
                >
                  <option value="">Select Item</option>
                  {items.map((i) => (
                    <option key={i.id} value={i.id}>{i.item}</option>
                  ))}
                </select>
              </Field>

              <Field label="Quantity">
                <input
                  type="text"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="w-full mt-2 h-11 border border-gray-200 rounded-xl px-4 bg-gray-50 text-sm outline-none focus:border-teal-400"
                />
              </Field>

              <Field label="Price per Item">
                <input
                  type="text"
                  inputMode="numeric"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="w-full mt-2 h-11 border border-gray-200 rounded-xl px-4 bg-gray-50 text-sm outline-none focus:border-teal-400"
                />
              </Field>

              <Field label="Amount">
                <input
                  value={amount}
                  readOnly
                  className="w-full mt-2 h-11 border border-gray-100 rounded-xl px-4 bg-gray-50 text-sm text-gray-500 cursor-not-allowed"
                />
              </Field>

              <Field label="Paid">
                <input
                  type="text"
                  inputMode="numeric"
                  value={paid}
                  onChange={(e) => setPaid(e.target.value)}
                  className="w-full mt-2 h-11 border border-gray-200 rounded-xl px-4 bg-gray-50 text-sm outline-none focus:border-teal-400"
                />
              </Field>

              <Field label="Remaining Balance">
                <input
                  value={balance}
                  readOnly
                  className="w-full mt-2 h-11 border border-gray-100 rounded-xl px-4 bg-gray-50 text-sm text-gray-500 cursor-not-allowed"
                />
              </Field>
            </div>

            {/* Note */}
            <Field label="Note">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full mt-2 border border-gray-200 rounded-xl p-3 bg-gray-50 text-sm outline-none focus:border-teal-400 resize-none"
              />
            </Field>

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
              <button
                onClick={() => navigate("/purchase-voucher")}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 rounded-xl text-white text-sm font-medium bg-teal-600 hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={14} />
                {saving ? "Saving..." : "Update Voucher"}
              </button>
            </div>

          </div>
        </div>

      </div>
    </main>
  );
}

function InfoCard({ label, value, highlight }) {
  return (
    <div className={`p-3 rounded-xl border ${highlight ? "bg-red-50 border-red-100" : "bg-white border-gray-100"}`}>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="font-semibold text-gray-800 mt-0.5">{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      {children}
    </div>
  );
}